/**
 * Type-Guards zur Unterscheidung von Angebot/Auftrag (`operation`) und
 * Auftragsbestätigung (`operation-out`).
 *
 * Hintergrund (belegt über api/collections/*.yml + api/hooks/lib/importer.js):
 * - `emailed`/`orderConfimationEmailed` liegen ausschließlich auf
 *   `OperationOutStatusSummary` (Kollektion `operation-out`).
 * - `canceled` liegt ausschließlich auf `OperationStatusSummary`
 *   (Kollektion `operation`).
 *
 * Die Union `Operation | OperationOut` trägt `collectionName`; zur Laufzeit
 * setzen getOperation/getOneOperation das Feld auf die tatsächliche Kollektion
 * (siehe frontend/src/api.ts).
 */

export function isOperationOut(op: Operation | OperationOut | null | undefined): op is OperationOut {
    return op?.collectionName === "operation-out"
}

export function isOperation(op: Operation | OperationOut | null | undefined): op is Operation {
    return op?.collectionName === "operation"
}

/**
 * Liefert eine operation-out-Repräsentation eines Objekts, dem die
 * `collectionName` noch fehlt.
 *
 * `createOperationOut` legt eine Operation als operation-out an; der
 * Server-Hook `operation-out/post_create.js` entscheidet dabei identisch:
 * `collectionName`, falls vorhanden – sonst über `refNumber`. Ein Objekt ohne
 * `collectionName` ist also ein operation-out-Kandidat, sobald es keine
 * `refNumber` aus einem Eingangsvorgang besitzt.
 *
 * Wichtig: Gibt bei fehlender `collectionName` eine flache Kopie zurück, um
 * das Original nicht zu mutieren. Der Aufrufer (z. B. die Konvertierung eines
 * Angebots) behält sein Objekt unverändert, falls der Vorgang fehlschlägt.
 */
export function toOperationOut<T extends Operation | OperationOut>(op: T): Extract<T, OperationOut> | OperationOut {
    if (!op) return op as OperationOut
    return op.collectionName ? (op as OperationOut) : ({ ...op, collectionName: "operation-out" } as unknown as OperationOut)
}

/**
 * Verengt eine bereits als `operation-out` bekannte Operation auf
 * `OperationOut`, ohne `collectionName` zu setzen oder zu kopieren.
 *
 * Anders als `toOperationOut` ist dies ein reiner Typ-Cast für Stellen, an
 * denen der `operation-out`-Kontext zur Laufzeit bereits feststeht (z. B.
 * innerhalb eines `{#if operation.collectionName === "operation-out"}`),
 * Svelte/TypeScript diesen aber nicht über das Template verengt.
 */
export function asOperationOut(op: Operation | OperationOut): OperationOut {
    return op as OperationOut
}

/**
 * Gegenstück zu `asOperationOut`: verengt auf `Operation`, für Stellen, an
 * denen der `operation`-Kontext zur Laufzeit feststeht (z. B. Warenkorb, der
 * nur Angebote/Aufträge enthält), Svelte/TypeScript das aber nicht erkennt.
 */
export function asOperation(op: Operation | OperationOut): Operation {
    return op as Operation
}
